import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import StudentForm, {
  emptyStudentForm,
  type StudentFormValues,
} from './components/StudentForm';

export default function NewStudentPage() {
  const navigate = useNavigate();
  const { profile, session } = useAuth();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (values: StudentFormValues) => {
    setError('');
    const ownerId = profile?.id ?? session?.user?.id;
    if (!ownerId) {
      setError('로그인 정보를 확인할 수 없어요.');
      return;
    }

    setSaving(true);
    try {
      const { data, error: insertError } = await supabase
        .from('students')
        .insert({
          owner_id: ownerId,
          name: values.name.trim(),
          school: values.school.trim(),
          grade: values.grade,
          parent_contact: values.parentContact.trim(),
          lesson_location: values.lessonLocation.trim(),
          target_type: values.targetType,
          target_school: values.targetSchool.trim(),
          desired_major: values.desiredMajor.trim(),
          desired_career: values.desiredCareer.trim(),
          class_days: values.classDays,
          class_times: values.classTimes,
          class_start_time: values.classTimes[values.classDays[0]]?.start ?? '',
          class_end_time: values.classTimes[values.classDays[0]]?.end ?? '',
          concept_book: values.conceptBook.trim(),
          calc_book: values.calcBook.trim(),
          type_book: values.typeBook.trim(),
          advanced_book: values.advancedBook.trim(),
          completed_books: values.completedBooks.trim(),
          tuition_fee: values.tuitionFee ? Number(values.tuitionFee) : 0,
          tuition_due_day: values.tuitionDueDay ? Number(values.tuitionDueDay) : 0,
          tuition_due_month_offset: values.tuitionDueMonthOffset === '-1' ? -1 : 0,
          first_lesson_date: values.firstLessonDate || null,
          goal_deadline: values.goalDeadline || null,
          goal_progress: values.goalProgress || null,
        })
        .select();
      if (insertError) throw insertError;
      const newId = (data as { id: string }[] | null)?.[0]?.id;
      navigate(newId ? `/students/${newId}` : '/students');
    } catch {
      setError('학생 등록에 실패했어요. 잠시 후 다시 시도해주세요.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-8 md:px-6 md:py-10">
      <button
        type="button"
        onClick={() => navigate('/students')}
        className="flex items-center gap-1 text-sm font-medium text-foreground-600 hover:text-foreground-900 whitespace-nowrap"
      >
        <i className="ri-arrow-left-line"></i>
        학생 목록
      </button>

      <h1 className="mt-4 text-2xl font-bold text-foreground-950">학생 등록</h1>
      <p className="mt-1.5 text-sm text-foreground-600">
        기본 정보와 수업 일정, 교재, 교습비를 입력하면 학생관리카드가 만들어져요.
      </p>

      <StudentForm
        initialValues={emptyStudentForm}
        submitLabel="학생 등록하기"
        submitIcon="ri-check-line"
        submitting={saving}
        error={error}
        onSubmit={handleSubmit}
        onCancel={() => navigate('/students')}
      />
    </div>
  );
}